import React from 'react';

export interface SegmentedControlItem<T extends string = string> {
  id: T;
  label: string;
  disabled?: boolean;
}

export interface SegmentedControlProps<T extends string = string> {
  items: SegmentedControlItem<T>[];
  activeId: T;
  onChange: (id: T) => void;
  /** `sm` for inside FilterBar (compact); `md` for standalone use. Default `sm`. */
  size?: 'sm' | 'md';
  ariaLabel?: string;
  className?: string;
}

/**
 * Pill-style segmented control. Use inside the FilterBar in place of a
 * sub-tab strip when the choice is a "view mode" rather than a full
 * sub-section (e.g. Warehouse Inbound / Outbound / Storage / Box history).
 */
export default function SegmentedControl<T extends string = string>({
  items,
  activeId,
  onChange,
  size = 'sm',
  ariaLabel,
  className,
}: SegmentedControlProps<T>) {
  const wrapperClass = [
    'admin-segmented-control',
    `admin-segmented-control--${size}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div className={wrapperClass} role="tablist" aria-label={ariaLabel}>
      {items.map((item) => {
        const isActive = item.id === activeId;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            disabled={item.disabled}
            className={
              isActive
                ? 'admin-segmented-control__option admin-segmented-control__option--active'
                : 'admin-segmented-control__option'
            }
            onClick={() => onChange(item.id)}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
